import {
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  makeStyles,
  Paper,
  Radio,
  RadioGroup,
  TextField,
} from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Check } from "@material-ui/icons";
import { format } from "date-fns";
import React, { createRef, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../../../context/auth";
import { useFormCombos } from "../../../../../hooks/useFormCombos";
import {
  getBySlotId,
  sendExtract,
} from "../../../../../services/finalDeliberations.service";
import { finalDeliberationOptions } from "../../../../../services/formCombos.service";
import {
  getDateFormats,
  getDateTimeLocale,
} from "../../../../../utils/get-date-time-locale";
import { Select } from "../../../../Common/Select";
import { Table } from "../../../../Common/Table";
import Textarea from "../../../../Common/Textarea";
import ToastMessage from "../../../../Common/ToastMessage";
import { checkAccess, getComplianceOpinionName } from "../../../utils/index";
import {
  SendExtractButton,
  SlotCommitteeItem,
  SlotCommitteeLink,
  SlotCommitteeName,
  SlotCommitteesLinksContainer,
  Title,
} from "./styles";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "1vh 1vw",
    paddingBottom: "0px",
  },
  button: {
    ...theme.typography.subtitle1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    minWidth: 180,
    minHeight: 50,
    borderRadius: 25,
  },
  spacer: {
    marginBottom: 20,
  },
  success: {
    backgroundColor: theme.palette.success.dark,
    borderRadius: 5,
    height: 50,
  },
  label: {
    ...theme.typography.subtitle1,
    color: theme.palette.primary.contrastText,
  },
  icon: {
    color: theme.palette.primary.contrastText,
    fontSize: 30,
    marginRight: 50,
  },
  titleModal: {
    color: "#031795",
    fontWeight: "bold",
    fontSize: "20px",
  },
}));

const ExtractModal = ({
  slotId,
  deliberationId,
  t,
  defaultEmails,
  viewMode,
  onSendExtractError,
  i18n,
  close,
}) => {
  const { userAccess } = useAuth();

  const { getCurrencies, currencies, modalityList } = useFormCombos();

  const approvalsTableColumns = [
    {
      title: t("forms.committeeName").toUpperCase(),
      field: "committee",
    },
    {
      title: t("menu.finalDeliberations.info.status").toUpperCase(),
      field: "status",
    },
    {
      title: t("forms.committee.labels.date").toUpperCase(),
      field: "meetDate",
    },
    {
      title: t("forms.minutes.actions.caveats").toUpperCase(),
      field: "caveats",
    },

    {
      title: t("slots.finalElevation").toUpperCase(),
      field: "finalElevation",
    },
  ];
  const actionsTableColumns = [
    {
      title: t("forms.committeeName").toUpperCase(),
      field: "committee",
    },
    {
      title: t("forms.minutes.actions.requesterName").toUpperCase(),
      field: "requester",
    },
    {
      title: t("forms.minutes.actions.responsibleName").toUpperCase(),
      field: "firstResponsible",
    },
    {
      title: t("forms.minutes.actions.secoundResponsibleName").toUpperCase(),
      field: "secoundResponsible",
    },
    {
      title: t("forms.minutes.actions.actionNumber").toUpperCase(),
      field: "actionNumber",
    },
    {
      title: t("forms.minutes.actions.action").toUpperCase(),
      field: "description",
    },
    {
      title: t("forms.minutes.actions.deadline").toUpperCase(),
      field: "deadline",
    },
  ];

  const classes = useStyles();
  const ref = createRef(null);
  const emailsRef = useRef();

  const [toastMessage, setToastMessage] = useState({
    message: "",
    type: "error",
    open: false,
  });
  const [slot, setSlot] = useState({});
  const [isEmailsErrored, setIsEmailsErrored] = useState(true);
  const [canSendExtract, setCanSendExtract] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userAccess) {
      const hasAccessToSendExtract = checkAccess(
        userAccess,
        "exportDeliberationExtract"
      );

      setCanSendExtract(hasAccessToSendExtract);

      const language = localStorage.getItem("i18nextLng");

      getCurrencies(language);

      if (!viewMode) {
        emailsRef.current.value = defaultEmails;
      }

      if (defaultEmails.length > 0) {
        setIsEmailsErrored(false);
      }

      getBySlotId(slotId).then(({ data: slot }) => {
        setSlot({
          ...slot,
          actions: slot.actions.map((action) => ({
            ...action,
            deadline: format(
              new Date(action.deadline),
              getDateFormats(i18n.language),
              {
                locale: getDateTimeLocale(i18n.language),
              }
            ),
            requester: action.anyoneCanRequest
              ? t("menu.slot.anyone")
              : action.requester,
          })),
          modalities: slot.modalities.map((modality) => ({
            id: modality.id,
            name: modality.modalityName,
          })),
          companies: slot.companies.map((company) => ({
            id: company.companyId,
            name: `${company.companyName} - ${company.companyAbbreviation}`,
          })),
          approvals: slot.approvals.map((approval) => ({
            id: approval.id,
            committee: approval.committeeName,
            meetDate: approval.meetDate
              ? format(
                  new Date(approval.meetDate),
                  getDateFormats(i18n.language),
                  {
                    locale: getDateTimeLocale(i18n.language),
                  }
                )
              : "",
            caveats: (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {approval.caveats.split("/").map((caveat) => (
                  <span>{caveat.length > 0 ? `- ${caveat}` : ""}</span>
                ))}
              </div>
            ),
            status:
              finalDeliberationOptions[i18n.language].find(
                (deliberation) => deliberation.id === approval.deliberation
              )?.name || `-`,
            date: approval.date
              ? format(new Date(approval.date), getDateFormats(i18n.language), {
                  locale: getDateTimeLocale(i18n.language),
                })
              : "-",
            finalElevation: approval.finalElevation ? <Check /> : "",
          })),
          slotCommitteeLinks: slot.slotCommittees.map((sc) => ({
            link: `${window.location.origin}/fileManagement?slotCommitteeId=${sc.id}`,
            name: sc.committeeName,
          })),
        });

        if (
          slot.requesterEmail &&
          !defaultEmails.includes(slot.requesterEmail)
        ) {
          emailsRef.current.value = `${slot.requesterEmail};${defaultEmails}`;
        }
      });
    }
  }, [slotId, viewMode, i18n]);

  const handleSendExtract = async () => {
    setLoading(true);

    const emails = emailsRef.current.value;

    if (emails.length <= 0) {
      setToastMessage({
        message: t("meeting.AttentesRequerid"),
        type: "error",
        open: true,
      });
      return;
    }

    const formattedRecipients = emails
      ?.split(";")
      ?.map((email) => ({
        address: email.replaceAll(" ", ""),
      }))
      .filter((e) => e.address !== "");

    const emailValidator =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    let isEmailsValid = true;

    formattedRecipients.forEach((email) => {
      if (!emailValidator.test(String(email.address).toLowerCase())) {
        isEmailsValid = false;
      }
    });

    if (!isEmailsValid) {
      setToastMessage({
        open: true,
        message: "Email inválido.",
        type: "error",
      });

      return;
    }

    var svgElements = document.body.querySelectorAll("svg");

    svgElements.forEach(function (item) {
      item.setAttribute("width", item.getBoundingClientRect().width);
      item.setAttribute("height", item.getBoundingClientRect().height);
      item.style.width = null;
      item.style.height = null;
    });

    const formData = new FormData();

    formData.append("emails", emails);
    formData.append("deliberationId", deliberationId);
    formData.append("slotId", slotId);

    sendExtract(formData)
      .then(({ error }) => {
        close();
        if (error) {
          onSendExtractError(error);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div>
      <ToastMessage
        message={toastMessage.message}
        type={toastMessage.type || "error"}
        open={toastMessage.open}
        close={() => setToastMessage({ ...toastMessage, open: false })}
      />
      <span
        style={{ float: "right", cursor: "pointer" }}
        onClick={() => {
          close();
        }}
      >
        [X] Fechar
      </span>
      <Paper
        ref={ref}
        style={{ width: "100%" }}
        elevation={1}
        className={classes.root}
      >
        <Title>Informações Gerais</Title>

        <Grid
          container
          direction="row"
          spacing={3}
          style={{ marginTop: "16px", marginBottom: "22px" }}
        >
          {!viewMode && (
            <Grid item xs={12}>
              <Textarea
                name="for"
                id="for"
                label="Para"
                error={isEmailsErrored}
                textAreaRef={emailsRef}
                onChange={(event) => {
                  if (!event.target.value) {
                    setIsEmailsErrored(true);
                  } else if (isEmailsErrored) {
                    setIsEmailsErrored(false);
                  }
                }}
              />
            </Grid>
          )}

          <Grid item lg={6} xs={6}>
            <Textarea
              disabled={true}
              name="theme"
              id="theme"
              value={slot.theme || ""}
              fullWidth
              label={t("slots.theme")}
              required
            />
          </Grid>

          <Grid item lg={6} xs={6}>
            <Select
              id="modality"
              options={modalityList ? modalityList : []}
              value={slot.modalities}
              getOptionLabel={(option) => `#${option.name}` || ""}
              label={t("slots.modality")}
              multiple={true}
              disabled
              renderTags={(selectedValues) => {
                return selectedValues.map((option) => (
                  <Chip
                    disabled
                    className={classes.chip}
                    key={option.id}
                    variant="outlined"
                    label={option.name}
                  />
                ));
              }}
            />
          </Grid>

          <Grid item lg={6} xs={6}>
            <Textarea
              disabled={true}
              name="objective"
              id="objective"
              value={slot.objective || ""}
              fullWidth
              required
              label={t("slots.objective")}
            />
          </Grid>

          <Grid item lg={6} xs={6}>
            <Select
              id="companies"
              options={slot.companies ?? []}
              getOptionSelected={(option, value) => option.id === value.id}
              value={slot.companies ?? []}
              label={t("slots.companies")}
              disabled={true}
              multiple={true}
              renderTags={(selectedValues) => {
                return selectedValues.map((option) => (
                  <Chip
                    className={classes.chip}
                    key={option.id}
                    onDelete={() => {}}
                    onClick={() => {}}
                    variant="outlined"
                    label={option.name}
                  />
                ));
              }}
            />
          </Grid>

          <Grid item lg={6} xs={6}>
            <TextField
              disabled={true}
              name="institution"
              id="institution"
              value={slot.intitution || ""}
              label={t("slots.institution")}
              fullWidth
            />
          </Grid>

          <Grid item lg={6} xs={6}>
            <Select
              disabled={true}
              id="currency"
              options={currencies || []}
              value={currencies.find((c) => c.id === slot.currency) || ""}
              label={t("menu.slot.currency")}
            />
          </Grid>

          <Grid item lg={6} xs={6}>
            <TextField
              disabled={true}
              name="value"
              id="value"
              value={slot.value || ""}
              label={t("value")}
              fullWidth
            />
          </Grid>

          <Grid item lg={6} xs={6}>
            <TextField
              disabled={true}
              name="additive"
              id="additive"
              value={slot.additive || ""}
              label={t("additive")}
              fullWidth
            />
          </Grid>

          <Grid item lg={6} xs={6}>
            <TextField
              disabled={true}
              name="amountNotBudgetedYear"
              id="amountNotBudgetedYear"
              value={slot.amountNotBudgetedYear || ""}
              label={t("amountNotBudgetedYear")}
              fullWidth
            />
          </Grid>

          <Grid item lg={6} xs={6}>
            <TextField
              disabled={true}
              name="approvedValue"
              id="approvedValue"
              value={slot.approvedValue || ""}
              label={t("approvedValue")}
              fullWidth
            />
          </Grid>

          <Grid item lg={6} xs={6}>
            <TextField
              disabled={true}
              name="complianceOpinion"
              id="complianceOpinion"
              value={getComplianceOpinionName(slot.complianceOpinion)}
              label={t("slots.complianceOpinion")}
              fullWidth
            />
          </Grid>

          <Grid item lg={6} xs={6}>
            <TextField
              disabled={true}
              name="sponsor"
              id="sponsor"
              value={slot.sponsor || ""}
              label={t("sponsor")}
              fullWidth
            />
          </Grid>

          <Grid item lg={6} xs={6}>
            <Select
              disabled={true}
              name="hosts"
              id="hosts"
              value={
                slot?.slotHosts
                  ?.split(" | ")
                  .map((item) => ({ id: 1, name: item })) || []
              }
              label={t("slots.presenter")}
              fullWidth
              options={
                slot?.slotHosts
                  ?.split(" | ")
                  .map((item) => ({ id: 1, name: item })) || []
              }
              multiple={true}
              renderTags={(selectedValues) => {
                return selectedValues.map((option) => (
                  <Chip
                    className={classes.chip}
                    key={option.id}
                    variant="outlined"
                    disabled
                    label={option.name}
                  />
                ));
              }}
            />
          </Grid>

          <Grid item lg={6} xs={6}>
            <TextField
              disabled={true}
              name="directorShip"
              id="directorShip"
              value={slot.directory || ""}
              label={t("slots.board")}
              fullWidth
            />
          </Grid>

          <Grid item lg={6} xs={6}>
            <TextField
              disabled={true}
              name="projectIdentifier"
              id="projectIdentifier"
              value={slot.projectIdentifier || ""}
              label={t("menu.slot.projectIdentifier")}
              fullWidth
            />
          </Grid>

          <Grid item lg={6} xs={6}>
            <SlotCommitteesLinksContainer>
              <FormLabel component="legend">{t("menu.finalDeliberations.modal.materialSupportLink")}</FormLabel>

              {slot.slotCommitteeLinks?.map((scl) => (
                <SlotCommitteeItem>
                  <SlotCommitteeName>{scl.name}</SlotCommitteeName>

                  <SlotCommitteeLink href={scl.link}>
                    {scl.link}
                  </SlotCommitteeLink>
                </SlotCommitteeItem>
              ))}
            </SlotCommitteesLinksContainer>
          </Grid>

          <Grid item lg={6} xs={6}>
            <FormControl component="fieldset">
              <FormLabel component="legend">
                {t("slots.budget.title")}
              </FormLabel>
              <RadioGroup
                disabled={true}
                aria-label="budget"
                name="budget"
                value={slot.budgeted || ""}
                required={true}
                row
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio size="small" />}
                  label={t("slots.budget.yes")}
                  disabled={true}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio size="small" />}
                  label={t("slots.budget.no")}
                  disabled={true}
                />
                <FormControlLabel
                  value="na"
                  control={<Radio size="small" />}
                  label={t("slots.budget.na")}
                  disabled={true}
                />
                <FormControlLabel
                  value="pno"
                  control={<Radio size="small" />}
                  label={t("slots.budget.pno")}
                  disabled={true}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>

        <div style={{ marginBottom: "16px" }}>
          <Title>Histórico de aprovações</Title>

          <Table columns={approvalsTableColumns}   data={slot.approvals} />
        </div>

        <div>
          <Title>{t("menu.actions.title")}</Title>

          <Table columns={actionsTableColumns} data={slot.actions} />
        </div>

        {!viewMode && (
          <SendExtractButton>
            <Button
              style={{ backgroundColor: "#40916c" }}
              className={classes.button}
              variant="contained"
              disabled={!canSendExtract || loading}
              onClick={handleSendExtract}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                t("menu.finalDeliberations.info.sendExtract").toUpperCase()
              )}
            </Button>
          </SendExtractButton>
        )}
      </Paper>
    </div>
  );
};

export default ExtractModal;
